import React, { useState, useRef, useEffect } from 'react';

const API_URL = '/api/content';

const ProductCard = ({ product }) => {
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-full">
      <div className="h-56 bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-black/20"></div>
        {product.image ? (
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-6xl opacity-50">🔩</span>
        )}
        <div className="absolute bottom-3 right-3 bg-pink-600 px-3 py-1 rounded-full">
          <span className="text-sm font-semibold text-white">{product.material}</span>
        </div>
      </div>
      
      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {product.name}
        </h3>
        <p className="text-gray-600 mb-4 flex-grow">
          {product.description}
        </p>
        
        <div className="border-t border-gray-200 pt-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {(product.processes || []).map((process, idx) => (
              <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                {process}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-700">Tolerance:</span> {product.tolerance}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            <span className="font-medium text-gray-700">Application:</span> {product.application}
          </p>
        </div>
      </div>
    </div>
  );
};

const Products = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [products, setProducts] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/products`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setProducts(data);
        }
      })
      .catch(err => console.error('Error fetching products:', err));
  }, []);

  useEffect(() => {
    if (products.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % products.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [products.length]);

  const getDisplaySlides = () => {
    if (products.length === 0) return [];
    const displaySlides = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentSlide + i) % products.length;
      displaySlides.push(products[index]);
    }
    return displaySlides;
  };

  if (products.length === 0) return null;

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 mb-8">
        <div className="text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="flex-1 h-px bg-gray-300"></div>
            <h2 className="text-4xl font-bold text-gray-900 px-8">
              OUR PRODUCTS
            </h2>
            <div className="flex-1 h-px bg-gray-300"></div>
          </div>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Wide range of precision machined parts for automotive, industrial, medical, and electronics applications. Materials include steel, aluminum, stainless steel, and titanium.
          </p>
        </div>
      </div>

      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="relative max-w-full mx-auto">
            <div ref={containerRef} className="relative overflow-hidden">
              <div className="flex gap-6 justify-center">
                {getDisplaySlides().map((product, index) => (
                  <div key={`${index}`} className="w-full md:w-1/2 lg:w-[calc(33.333%-16px)] flex-shrink-0 px-2">
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>

              {/* Dots Navigation */}
              <div className="flex justify-center gap-2 mt-8">
                {products.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-3 rounded-full transition-all ${
                      currentSlide === index 
                        ? 'bg-pink-600 w-8' 
                        : 'bg-gray-300 hover:bg-gray-400 w-3'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Products;
